import React, { useState } from "react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { FaFacebookF, FaLinkedin } from "react-icons/fa";
import { AiFillGithub, AiOutlineTwitter } from "react-icons/ai";
import { useSelector } from "react-redux";
import { AiFillShopping, AiFillHeart } from "react-icons/ai";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import toast from "react-hot-toast"; // Import toast for notifications
import api from "../api/api";

const Footer = () => {
  const { card_product_count, wishlist_count } = useSelector(
    (state) => state.card
  );
  const { seller } = useSelector((state) => state.home);
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const { storeName, storeid } = useParams();
  const location = useLocation();

  const isStorePage = storeName || storeid || location.state?.fromStore;
  const effectiveStoreName = storeName || seller?.shopInfo?.shopName;

  // State for email input
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("You are subscribed 🎉");

  // Handle subscription
  const handleSubscribe = async () => {
    if (email) {
      setLoading(true);
      if (effectiveStoreName) {
        try {
          await api.post('/home/store/subscribe', { email, storeName: effectiveStoreName });
          setEmail("");
          toast.success("Thank you for your subscription");
          setSubscriptionMessage("You are subscribed 🎉");
          setIsSubscribed(true);
        } catch (error) {
          if (error.response?.data?.error === 'Already subscribed') {
            setEmail("");
            toast.success("You are already subscribed");
            setSubscriptionMessage("You are already subscribed 🎉");
            setIsSubscribed(true);
          } else {
            toast.error(error.response?.data?.error || "Subscription failed");
          }
        } finally {
          setLoading(false);
        }
      } else {
        // Fallback for platform subscription if needed
        setTimeout(() => {
          setEmail("");
          toast.success("Thank you for your subscription");
          setSubscriptionMessage("You are subscribed 🎉");
          setIsSubscribed(true);
          setLoading(false);
        }, 1000);
      }
    } else {
      // Show an error toast if the email is empty
      toast.error("Please enter a valid email.");
    }
  };

  return (
    <footer className="bg-[var(--store-footer-bg)] text-[var(--store-text)]">
      <div className="w-[85%] flex flex-wrap mx-auto py-16 sm:gap-10 md-lg:pb-20 sm:pb-20">
        <div className="w-3/12 lg:w-4/12 sm:w-full">
          <div className="flex flex-col gap-5">
            <img
              className="w-[35px] h-auto sm:w-[25px] [.light-theme_&]:brightness-0"
              src={`${process.env.REACT_APP_URL}/images/mps.svg`}
              alt="logo"
            />
            <ul className="flex flex-col gap-2 text-[var(--store-muted)] text-sm">
              {isStorePage ? (
                <li>
                  Powered by <a href="https://mypursepay.com" target="_blank" rel="noopener noreferrer" className="font-bold text-[var(--store-text)] ">MyPursePay</a>
                </li>
              ) : (
                <li>Email - support@mypursepay.com</li>
              )}
            </ul>
          </div>
        </div>
        <div className="w-5/12 lg:w-8/12 sm:w-full">
          <div className="flex justify-center sm:justify-start sm:mt-6 w-full">
            <div>
              <h2 className="font-bold text-lg mb-3">Company</h2>
              <div className="flex justify-between gap-[80px] lg:gap-[40px]">
                <ul className="flex flex-col gap-2 text-[var(--store-muted)] text-sm">
                  {isStorePage ? (
                    <>
                      <li>
                        <Link to={`/${effectiveStoreName}/about-us`}>About Us</Link>
                      </li>
                      <li>
                        <Link to={`/${effectiveStoreName}/privacy-policy`}>Privacy Policy</Link>
                      </li>
                      <li>
                        <Link to={`/${effectiveStoreName}/return-refund-policy`}>Return & Refund Policy</Link>
                      </li>
                      <li>
                        <Link to={`/${effectiveStoreName}/terms-and-conditions`}>Terms & Conditions</Link>
                      </li>
                    </>
                  ) : (
                    <>
                      <li>
                        <Link to="/about-us">About Us</Link>
                      </li>
                      <li>
                        <Link to="/privacy-policy">Privacy Policy</Link>
                      </li>
                      <li>
                        <Link to="/return-refund-policy">Return & Refund Policy</Link>
                      </li>
                      <li>
                        <Link to="/terms-and-conditions">Terms & Conditions</Link>
                      </li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="w-4/12 lg:w-full lg:mt-6">
          <div className="w-full flex flex-col justify-start gap-3">
            {isSubscribed ? (
              <h2 className="font-bold text-lg text-green-500">{subscriptionMessage}</h2>
            ) : (
              <>
                <h2 className="font-bold text-lg">Get updates from us</h2>
                <span className="text-[var(--store-muted)] text-sm">
                  Get email updates about our latest and shop specials offers
                </span>
                <div className="flex w-full max-w-sm items-center space-x-2">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    className="bg-[var(--store-input-bg)] text-[var(--store-input-text)] border-[var(--store-input-border)] placeholder:text-[var(--store-input-text)] store-input-focus"
                    style={{ borderRadius: 'var(--store-input-radius, 20px)', borderWidth: 'var(--store-input-border-width, 1px)' }}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Button
                    type="submit"
                    onClick={handleSubscribe}
                    disabled={loading}
                    className="bg-[var(--store-button-bg)] text-[var(--store-button-text)] hover:bg-[var(--store-button-bg)]/90 disabled:opacity-70"
                  >
                    {loading ? "Subscribing..." : "Subscribe"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="w-[85%] flex flex-wrap justify-center items-center text-[var(--store-muted)] mx-auto py-5 text-center text-sm ">
        <span>Copyright ©2025 All rights reserved </span>
      </div>
    </footer>
  );
};

export default Footer;